import React, { useEffect } from "react";
import "../App.css";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AuthLayout from "../pages/common/layout";
import Goals from "../pages/admin/Goals";
import Users from "../pages/admin/Users";
import FreeVotes from "../pages/admin/FreeVotes";
import Tournament from "../pages/admin/Tournaments";
import Clubs from "../pages/admin/Clubs";
import Leagues from "../pages/admin/Leagues";
import Leaderboard from "../pages/admin/Leaderboards";
import ProtectedRoute from "../helper/protectedRoute";
import ProtectedRouteUser from "../helper/protectedRouteUser";
import SignIn from "../auth/signin";
import NotFound from "../pages/404";
import NotFoundComp from "../pages/404Comp";
import Register from "../authUser/Register";
import Login from "../authUser/Login";
import BoardingLayout from "../authUser/common/Layout";
import Home from "../pages/user/Home";
import CompetitionsList from "../pages/user/CompetitionsList";
import UserGoals from "../pages/user/Goals";
import UserLeaderboards from "../pages/user/Leaderboards";
import MyAccount from "../pages/user/MyAccount";
import Settings from "../pages/user/Settings";
import ForgetPassword from "../authUser/ForgetPassword";
import RessetPassword from "../authUser/RessetPassword";
import SplashScreen from "../authUser/SplashScreen";
import Test from "../pages/test";
import AddGoal from "../pages/user/AddGoal";
import EditGoal from "../pages/user/EditGoal";
import EditChallenge from "../pages/user/EditChallenge";
import AddTournament from "../pages/user/AddTournament";
import EditTournament from "../pages/user/EditTournament";
import EditClub from "../pages/user/EditClub";
import EditLeague from "../pages/user/EditLeague";
import HelperRedirect from "../helper/HelperRedirect";
import ProfessionalTournament from "../pages/admin/ProfessionalTournament";
import AddProfessionalTournament from "../pages/admin/AddProfessionalTournament";
import Banners from "../pages/admin/Banners";
import HomeBanners from "../pages/admin/HomeBanners";
import YoutubeLinks from "../pages/admin/YoutubeLinks";
import AddBanner from "../pages/admin/AddBanner";
import AddHomeBanner from "../pages/admin/AddHomeBanner";
import AddYoutubeLinks from "../pages/admin/AddYoutubeLinks";
import EditBanner from "../pages/admin/EditBanner";
import EditHomeBanner from "../pages/admin/EditHomeBanner";
import Advertisements from "../pages/admin/Advertisements";
import AddAdvertisement from "../pages/admin/AddAdvertisement";
import EditAdvertisement from "../pages/admin/EditAdvertisement";
import Competitions from "../pages/admin/Competitions";
import AddCompetition from "../pages/admin/AddCompetition";
import EditCompetition from "../pages/admin/EditCompetition";
import CompetitionVideos from "../pages/admin/CompetitionVideos";
import CompetitionDetail from "../pages/user/CompetitionDetail";
import CompetitionBuyVotesPage from "../pages/user/CompetitionBuyVotesPage";
import CompetitionBuyVotesReturn from "../pages/user/CompetitionBuyVotesReturn";
import CompetitionEnter from "../pages/user/CompetitionEnter";
import CompetitionVideosPage from "../pages/user/CompetitionVideosPage";
import CompetitionLeaderboardPage from "../pages/user/CompetitionLeaderboardPage";
import CompetitionFinalNightAdmin from "../pages/admin/CompetitionFinalNight";
import AdminCompetitionRegister from "../pages/admin/AdminCompetitionRegister";
import CompetitionVoteAdjust from "../pages/admin/CompetitionVoteAdjust";
import VotePurchases from "../pages/admin/VotePurchases";
import CompetitionFinalNight from "../pages/user/CompetitionFinalNight";
import EditYoutubeLinks from "../pages/admin/EditYoutubeLinks";
import EditProfessionalTournament from "../pages/admin/EditProfessionalTournament";
import Notifications from "../pages/user/Notification";
import Categories from "../pages/admin/Categories";
import AddChallenge from "../pages/user/AddChallenge";
import ChallengeCategories from "../pages/admin/ChallengeCategories";
import TournamentCategories from "../pages/admin/TournamentCategories";
import Analytics from "../pages/admin/Analytics";
import FanzoneLeaderboard from "../pages/admin/Fanzone";
import FeaturedLinks from "../pages/admin/FeaturedLinks";
import UserProfessionalTournament from "../pages/user/ProfessionalTournament";
import TermsConditions from "../pages/user/termsAndConditions/TermsConditions";
import ViewTournament from "../pages/user/ViewTournament";
import ViewClub from "../pages/user/ViewClub";
import ViewLeague from "../pages/user/ViewLeague";
import AddClub from "../pages/user/AddClub";
import AddLeague from "../pages/user/AddLeague";
import AppConfiguration from "../pages/admin/AppConfiguration";
import PrivacyPolicy from "../pages/user/PrivacyPolicy"

const FashionDesigners = React.lazy(() =>
  import("../pages/admin/fashionDesigners/FashionDesignersList")
);
const AddFashionDesigner = React.lazy(() =>
  import("../pages/admin/fashionDesigners/AddFashionDesigner")
);
const EditFashionDesigner = React.lazy(() =>
  import("../pages/admin/fashionDesigners/EditFashionDesigner")
);
const FashionDesignerDesigns = React.lazy(() =>
  import("../pages/admin/fashionDesigners/FashionDesignerDesigns")
);
const FashionDesignEditor = React.lazy(() =>
  import("../pages/admin/fashionDesigners/FashionDesignEditor")
);
const FashionBanner = React.lazy(() =>
  import("../pages/admin/fashionDesigners/FashionBanner")
);
const FashionShowcase = React.lazy(() => import("../pages/user/fashion/FashionShowcase"));
const FashionLeaderboardPage = React.lazy(() =>
  import("../pages/user/fashion/FashionLeaderboardPage")
);
const SubmitFashionDesign = React.lazy(() => import("../pages/user/fashion/SubmitFashionDesign"));
const FashionVoteReturn = React.lazy(() => import("../pages/user/fashion/FashionVoteReturn"));
const FashionBuyVotesPage = React.lazy(() =>
  import("../pages/user/fashion/FashionBuyVotesPage")
);

function LazyAdminPage({ children }) {
  return (
    <React.Suspense fallback={null}>{children}</React.Suspense>
  );
}

function AdminRoutes() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const navigate = useNavigate();
  return (
    <Routes>
      <Route path="admin/authenticate" element={<SignIn />} />
      <Route
        path="admin/dashboard"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Goals />
              </AuthLayout>
            }
          />
        }
      />

      <Route path="admin" element={<HelperRedirect />} />

      <Route
        path="admin/dashboard/goals"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Goals />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/users"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Users />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/analytics"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Analytics />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/tournaments"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Tournament />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/clubs"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Clubs />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/leagues"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Leagues />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/free-votes"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <FreeVotes />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/leaderboards"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Leaderboard />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/fanzone"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <FanzoneLeaderboard />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/international-tournament"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <ProfessionalTournament />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/international-tournament/create-international-tournament"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AddProfessionalTournament />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/international-tournament/edit-international-tournament/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <EditProfessionalTournament />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/banners"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Banners />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/banners/add-banner"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AddBanner />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/banners/edit-banner/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <EditBanner />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/home-banners"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <HomeBanners />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/home-banners/add-banner"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AddHomeBanner />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/home-banners/edit-banner/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <EditHomeBanner />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/advertisements"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Advertisements />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/advertisements/add"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AddAdvertisement />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/advertisements/edit/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <EditAdvertisement />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/competitions"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Competitions />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/competitions/add"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AddCompetition />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/competitions/edit/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <EditCompetition />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/competitions/:id/register"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AdminCompetitionRegister />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/competition-videos"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <CompetitionVideos />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/vote-payments"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <VotePurchases />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/competitions/:id/vote-adjust"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <CompetitionVoteAdjust />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/competitions/final-night/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <CompetitionFinalNightAdmin />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/fashion-banner"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <LazyAdminPage>
                  <FashionBanner />
                </LazyAdminPage>
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/fashion-designers"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <LazyAdminPage>
                  <FashionDesigners />
                </LazyAdminPage>
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/fashion-designers/add"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <LazyAdminPage>
                  <AddFashionDesigner />
                </LazyAdminPage>
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/fashion-designers/:designerId/designs"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <LazyAdminPage>
                  <FashionDesignerDesigns />
                </LazyAdminPage>
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/fashion-designers/:designerId/designs/add"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <LazyAdminPage>
                  <FashionDesignEditor />
                </LazyAdminPage>
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/fashion-designers/:designerId/designs/edit/:designId"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <LazyAdminPage>
                  <FashionDesignEditor edited />
                </LazyAdminPage>
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/fashion-designers/edit/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <LazyAdminPage>
                  <EditFashionDesigner />
                </LazyAdminPage>
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/youtube-links"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <YoutubeLinks />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/youtube-links/add-youtube-links"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AddYoutubeLinks />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/youtube-links/edit-youtube-links/:id"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <EditYoutubeLinks />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/goal-categories"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Categories />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/goal-categories/:create"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Categories />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/goal-categories/:edit/:name"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <Categories />
              </AuthLayout>
            }
          />
        }
      />

      <Route
        path="admin/dashboard/challenge-categories"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <ChallengeCategories />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/tournament-categories"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <TournamentCategories />
              </AuthLayout>
            }
          />
        }
      />
      <Route
        path="admin/dashboard/featured-link"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <FeaturedLinks />
              </AuthLayout>
            }
          />
        }
      />



      <Route
        path="admin/dashboard/app-configuration"
        element={
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            element={
              <AuthLayout>
                <AppConfiguration />
              </AuthLayout>
            }
          />
        }
      />

      {/* user routes  */}

      {/* auth routes */}

      <Route
        path="/register"
        element={
          <BoardingLayout>
            {" "}
            <Register />{" "}
          </BoardingLayout>
        }
      />

      <Route
        path="/login"
        element={
          <BoardingLayout>
            <Login />
          </BoardingLayout>
        }
      />

      <Route
        path="/privacy-policy"
        element={
          <BoardingLayout>
            <PrivacyPolicy />
          </BoardingLayout>
        }
      />

      <Route
        path="/forgotpassword"
        element={
          <BoardingLayout>
            <ForgetPassword />
          </BoardingLayout>
        }
      />

      <Route
        path="/resetpassword"
        element={
          <BoardingLayout>
            <RessetPassword />
          </BoardingLayout>
        }
      />

      <Route
        path="/welcome"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<SplashScreen />}
          />
        }
      />

      <Route path="/termsandconditions" element={<TermsConditions />} />

      <Route
        path="/"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<Home />}
          />
        }
      />

      <Route
        path="/competitions"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<CompetitionsList />}
          />
        }
      />

      <Route
        path="/fashion-designers/votes/return"
        element={
          <LazyAdminPage>
            <FashionVoteReturn />
          </LazyAdminPage>
        }
      />

      <Route
        path="/fashion-designers/submit"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={
              <LazyAdminPage>
                <SubmitFashionDesign />
              </LazyAdminPage>
            }
          />
        }
      />

      <Route path="/fashion-designers" element={<Navigate to="/competitions" replace />} />

      <Route
        path="/fashion-designers/:designerId/leaderboard"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={
              <LazyAdminPage>
                <FashionLeaderboardPage />
              </LazyAdminPage>
            }
          />
        }
      />

      <Route
        path="/fashion-designers/:designerId/buy-votes"
        element={
          <LazyAdminPage>
            <FashionBuyVotesPage />
          </LazyAdminPage>
        }
      />

      <Route
        path="/fashion-designers/:designerId"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={
              <LazyAdminPage>
                <FashionShowcase />
              </LazyAdminPage>
            }
          />
        }
      />

      <Route
        path="/competitions/:id/buy-votes/return"
        element={<CompetitionBuyVotesReturn />}
      />

      <Route
        path="/competitions/:id/buy-votes"
        element={<CompetitionBuyVotesPage />}
      />

      <Route
        path="/competitions/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<CompetitionDetail />}
          />
        }
      />

      <Route
        path="/competitions/:id/enter"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<CompetitionEnter />}
          />
        }
      />

      <Route
        path="/competitions/:id/videos"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<CompetitionVideosPage />}
          />
        }
      />

      <Route
        path="/competitions/:id/leaderboard"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<CompetitionLeaderboardPage />}
          />
        }
      />

      <Route
        path="/competitions/:id/final-night"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<CompetitionFinalNight />}
          />
        }
      />

      <Route
        path="/goals"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<UserGoals />}
          />
        }
      />

      <Route
        path="leaderboards/:id?/:type?"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<UserLeaderboards />}
          />
        }
      />

      <Route
        path="myaccount/:set?"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<MyAccount />}
          />
        }
      />

      <Route
        path="notifications"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<Notifications />}
          />
        }
      />

      <Route
        path="settings"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<Settings />}
          />
        }
      >
        <Route path=":page" element={<Settings />} />
      </Route>

      <Route
        path="addGoal"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<AddGoal />}
          />
        }
      />

      <Route
        path="editGoal/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<EditGoal />}
          />
        }
      />
      <Route
        path="editChallenge/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<EditChallenge />}
          />
        }
      />

      <Route
        path="addChallenge"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<AddChallenge />}
          />
        }
      />

      <Route
        path="addTournament"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<AddTournament />}
          />
        }
      />
      <Route
        path="addClub"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<AddClub />}
          />
        }
      />
      <Route
        path="addLeague"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<AddLeague />}
          />
        }
      />

      <Route
        path="editTournament/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<EditTournament />}
          />
        }
      />

      <Route
        path="editClub/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<EditClub />}
          />
        }
      />

      <Route
        path="editLeague/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<EditLeague />}
          />
        }
      />

      <Route
        path="viewTournament/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<ViewTournament />}
          />
        }
      />

      <Route
        path="viewClub/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<ViewClub />}
          />
        }
      />

      <Route
        path="viewLeague/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<ViewLeague />}
          />
        }
      />

      <Route
        path="*"
        element={
          <BoardingLayout>
            <NotFoundComp />
          </BoardingLayout>
        }
      />

      <Route
        path="not-found"
        element={
          <BoardingLayout>
            <NotFoundComp />
          </BoardingLayout>
        }
      />

      <Route
        path="professional-tournament/:id"
        element={
          <ProtectedRouteUser
            isAuthenticated={isAuthenticated}
            element={<UserProfessionalTournament />}
          />
        }
      />

      <Route path="test" element={<Test />} />
    </Routes>
  );
}

export default AdminRoutes;
